import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { refetchAllAppointmentsApis } from "../../../api/Appointments/refetchAllAppointmentsApis";
import { GeneralButton, Modal } from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { useGetData, usePostData } from "../../../hooks";
import {
  GetSchedule,
  IDays,
  IScheduleItem,
} from "../../../interface/GeneralResponse";
import { useToast } from "../../../Services/ToastContext";
import Schedule from "./Schedule";
import SingleDay from "./SingleDay";
import warning from "../../../assets/images/icons/alert-triangle.svg";
import { EditProfileLayout } from "../component";

interface IShiftState {
  [DayId: number]: {
    shifts: IScheduleItem[];
    isActive: boolean;
  };
}

function ScheduleListAdd() {
  const { showToast } = useToast();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: scheduleData, refetch } = useGetData<GetSchedule>(
    EndPoints.GetSchedule,
  );
  const scheduleDataAPI = scheduleData?.Data?.Schedule;
  const { mutate, isPending } = usePostData(EndPoints.EditSchedule);

  const { data: dayData } = useGetData<IDays[]>(EndPoints.Days);
  const daysData = dayData?.Data || [];

  const [shifts, setShifts] = useState<IShiftState>({});
  const [initialShifts, setInitialShifts] = useState<IShiftState>({});
  const [modalAction, setModalAction] = useState<"save" | "edit">("save");
  const [errors, setErrors] = useState<{ [dayId: number]: boolean }>({});
  const [isFirstTimeOpen, setIsFirstTimeOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(
    scheduleData?.Data?.InspectionDuration == null ? false : true,
  );
 

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (Object.keys(initialShifts).length === 0 && daysData.length > 0) {
      const initial: IShiftState = {};
      if (scheduleDataAPI && scheduleDataAPI.length > 0) {
        scheduleDataAPI.forEach((shift) => {
          const formattedShifts: IScheduleItem[] = shift.ScheduleDetails.map(
            (detail) => ({
              DayId: shift.DayId,
              From: detail.FromTime,
              To: detail.ToTime,
              IsActive: shift.IsActive,
            }),
          );

          if (initial[shift.DayId]) {
            initial[shift.DayId].shifts.push(...formattedShifts);
          } else {
            initial[shift.DayId] = {
              shifts: formattedShifts,
              isActive: shift.IsActive,
            };
          }
        });
      }

      daysData.forEach((day) => {
        if (!initial[day.Id]) {
          initial[day.Id] = {
            shifts: [],
            isActive: false,
          };
        }
      });

      setShifts(initial);
      setInitialShifts(initial);
    }
  }, [scheduleDataAPI, daysData, initialShifts]);

  const formatTime = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");
    return `${formattedHours}:${formattedMinutes}`;
  };

  const clearError = async () => {
    setErrors({});
  };

  const handleSubmit = async () => {
    const newErrors: { [dayId: number]: boolean } = {};
    Object.entries(shifts).forEach(([dayId, dayData]) => {
      if (dayData.isActive && dayData.shifts.length === 0) {
        newErrors[Number(dayId)] = true;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    const scheduleDataState = Object.entries(shifts)
      .filter(([_, dayData]) => dayData.isActive)
      .flatMap(([dayId, dayData]) => {
        const { shifts: dayShifts, isActive } = dayData;
        return dayShifts.map((shift: IScheduleItem) => ({
          DayId: Number(dayId),
          From: formatTime(shift.From),
          To: formatTime(shift.To),
          IsActive: isActive,
        }));
      });
    const scheduleDataJSON = JSON.stringify({ Schedule: scheduleDataState });
    mutate(scheduleDataJSON, {
      onSuccess: () => {
        showToast(t("added_successfully"), "success");
        refetch();
        refetchAllAppointmentsApis(queryClient);
        queryClient.refetchQueries({
          queryKey: [EndPoints.GetScheduledDays],
          exact: false,
        });
        queryClient.refetchQueries({
          queryKey: [EndPoints.GetPersonalInfo],
          exact: false,
        });
        queryClient.refetchQueries({
          queryKey: [EndPoints.GetAvailableScheduled],
          exact: false,
        });
        navigate("/profile/clinic_schedule");
      },
      onError: (error: Error) => {
        showToast(error.message, "error");
      },
    });
  };

  const handleDiscardClick = () => {
    navigate("/profile/clinic_schedule");
  };

  const setDayActive = (DayId: number, isActive: boolean) => {
    setShifts((prevShifts) => ({
      ...prevShifts,
      [DayId]: {
        ...(prevShifts[DayId] || { shifts: [] }),
        isActive,
      },
    }));
  };

  const addShift = (DayId: number, newShift: IScheduleItem) => {
    setShifts((prevShifts) => ({
      ...prevShifts,
      [DayId]: {
        ...(prevShifts[DayId] || { isActive: false, shifts: [] }),
        shifts: [...(prevShifts[DayId]?.shifts || []), newShift],
      },
    }));
  };

  const removeShift = (DayId: number, index: number) => {
    setShifts((prevShifts) => {
      const dayData = prevShifts[DayId];
      if (!dayData) return prevShifts;
      const newShifts = [...dayData.shifts];
      newShifts.splice(index, 1);
      return {
        ...prevShifts,
        [DayId]: {
          ...dayData,
          shifts: newShifts,
        },
      };
    });
  };

const addShiftToAllDays = (
  _sourceDayId: number,
  newShift: IScheduleItem,
): string | null => {
  const newStart = moment(newShift.From, "HH:mm");
  const newEnd = moment(newShift.To, "HH:mm");
 
  const hasConflictInAnyDay = daysData.some((day: IDays) => {
    const existingShifts: IScheduleItem[] = shifts[day.Id]?.shifts || [];

    return existingShifts.some(
      (shift: IScheduleItem) =>
        moment(shift.From, "HH:mm").isBefore(newEnd) &&
        moment(shift.To, "HH:mm").isAfter(newStart),
    );
  });

  if (hasConflictInAnyDay) {
    return t("this_time_includes_in_other_days");
  }

  setShifts((prevShifts) => {
    const newShiftsState: IShiftState = {
      ...prevShifts,
    };
 
    daysData.forEach((day: IDays) => {
      const existingShifts: IScheduleItem[] = prevShifts[day.Id]?.shifts || [];

      newShiftsState[day.Id] = {
        shifts: [
          ...existingShifts,
          {
            ...newShift,
            DayId: day.Id,
            IsActive: true,
          },
        ],
        isActive: true,
      };
    });

    return newShiftsState;
  });

  return null;
};

  const handleEditScheduleClick = () => {
    if (isDisabled) {
      setModalAction("edit");
      setIsFirstTimeOpen(true);
    }
  };
  const handleNextClick = () => {
    if (isDisabled) {
      setIsDisabled(false);
      setIsFirstTimeOpen(false);
    } else {
      setIsFirstTimeOpen(false);
    }
  };

  const handleSaveClick = () => {
    if (scheduleData?.Data?.Schedule?.length === 0) {
      void clearError().then(() => {
        handleSubmit();
      });

      return;
    }

    setModalAction("save");
    setIsFirstTimeOpen(true);
  };
  return (
    <>
      <EditProfileLayout
        title={t("clinic_schedule")} 
        onDiscard={handleDiscardClick}
        onSave={handleSaveClick}
        isSaving={isPending}  
      >
        <div className="flex h-full min-h-0 w-full flex-col gap-4">
          <div className="shrink-0">
            <Schedule
              durationTime={scheduleData?.Data?.InspectionDuration}
              onSuccess={() => {
                refetch();
                setIsDisabled(true);
              }}
              isDisabled={isDisabled}
              onClick={handleEditScheduleClick}
            />
          </div>

          {scheduleDataAPI && scheduleData?.Data?.InspectionDuration > 0 && (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="flex flex-col gap-4 p-4">
                {daysData.map((day) => {
                  const dayId = day.Id;

                  const dayData = shifts[dayId] || {
                    shifts: [],
                    isActive: false,
                  };

                  return (
                    <div
                      key={day.Id}
                      className="border-b border-radio pb-4 last:border-none last:pb-0"
                    >
                      <SingleDay
                        Id={day.Id}
                        Name={day.Name}
                        DisplayName={day.Name}
                        isActive={dayData.isActive}
                        dayData={dayData}
                        setDayActive={setDayActive}
                        addShift={addShift}
                        addShiftToAllDays={addShiftToAllDays}
                        removeShift={removeShift}
                        validationNumber={
                          scheduleData?.Data?.InspectionDuration
                        }
                        showError={errors[dayId]}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </EditProfileLayout>
      {isFirstTimeOpen && (
        <Modal
          showClose={false}
          isOpen={isFirstTimeOpen}
          onClose={() => {
            setIsFirstTimeOpen(false);
            setIsDisabled(true);
          }}
        >
          <div className="flex flex-col gap-6 text-center">
            <img src={warning} alt="warning" className="mx-auto h-20 w-20" />
            <p className="text-lg font-bold">{t("change_session_duration")}</p>
            <p className="text-sm text-body-text">
              {t("session_duration_warning_message")}
            </p>
            <div className="flex items-center justify-end gap-3">
              <GeneralButton
                color="lite"
                title={t("cancel")}
                onClick={() => setIsFirstTimeOpen(false)}
              />
              <GeneralButton
                color="red"
                title={t("change_anyway")}
                onClick={() => {
                  if (modalAction === "save") {
                    clearError().then(() => handleSubmit());
                  } else {
                    handleNextClick();
                  }
                }}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default ScheduleListAdd;
